// src/features/landing/LandingClient.jsx
"use client";

import { useState, useEffect, useMemo } from "react";
import { useRouter } from "next/navigation";
import { toast } from "react-toastify";
import { useAuth } from "@/context/AuthContext";
import { usePiSocketCtx } from "@/context/PiSocketContext";
import CategoryTabs from "@/features/landing/components/CategoryTabs";
import VoucherSection from "@/features/landing/components/VoucherSection";
import TopupSection from "@/features/landing/components/TopupSection";
import EsimSectionClient from "@/features/landing/components/EsimSectionClient";
import { isBlocked } from "@/features/landing/utils/blocklist";
import usePiBrowser from "@/features/landing/hooks/usePiBrowser";
import SelectedProductSummary from "@/components/SelectedProductSummary";
import SiteUpdatesCarouselClient from "@/components/SiteUpdatesCarouselClient";
import { fetchBrandsMap } from "@/utils/api/brands";
import PiBrowserCta from "@/components/PiBrowserCta";


export default function LandingClient({
  defaultCategory = "vouchers",
  esimInitial,
  esimAllOffers,
  voucherInitial,
  topupInitial,
}) {

 
  const { user } = useAuth();
  const router = useRouter();

  const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
  const [selectedProduct, _setSelectedProduct] = useState(null);
  const [brands, setBrands] = useState({});
  const [featuredBrands, setFeaturedBrands] = useState([]);

  const { piRate, basePI, totalPI, feePercent, requestPriceUpdate } = usePiSocketCtx();
  const isPiBrowser = usePiBrowser();

  const setSelectedProduct = (p) => {
    if (p && isBlocked(p)) {
      toast.warn("This supplier is temporarily unavailable.");
      return; // don’t open the drawer for blocked items
    }
    _setSelectedProduct(p);
  };

  // helper to handle category select, including ebooks redirect
  const handleCategorySelect = (key) => {
    if (key === "ebooks") {
      router.push("/ebooks");
      return;
    }
    setSelectedCategory(key);
  };

  const priceUSD = useMemo(() => {
    return selectedProduct?.price?.fixed
      ? selectedProduct.price.fixed / 100
      : "N/A";
  }, [selectedProduct]);

  useEffect(() => {
  document.body.style.overflow = selectedProduct ? "hidden" : "";
  return () => { document.body.style.overflow = ""; };
}, [selectedProduct]);

useEffect(() => {
  if (!selectedProduct?.offerId) return;
  const usd = Number(priceUSD);
  if (!Number.isFinite(usd) || usd <= 0) return;

  requestPriceUpdate(usd, selectedProduct.offerId);
}, [priceUSD, selectedProduct?.offerId, requestPriceUpdate]);

useEffect(() => {
  let alive = true;

  (async () => {
    try {
      const map = await fetchBrandsMap(); // ✅ single request
      if (alive) setBrands(map || {});
    } catch (err) {
      console.error("❌ Failed to load brands map:", err);
    }
  })();

  return () => {
    alive = false;
  };
}, []);

useEffect(() => {
  let alive = true;

  (async () => {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/featured/brands?limit=8`,
        { cache: "no-store" }
      );

      const data = res.ok ? await res.json() : [];
      if (alive) setFeaturedBrands(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ Failed to load featured brands:", err);
      if (alive) setFeaturedBrands([]);
    }
  })();

  return () => {
    alive = false;
  };
}, []);

  return (
    <>
      <div className="px-4 sm:px-6 pt-4 pb-10">
        <div className="w-full">
          {/* HERO + CATEGORY TABS */}
          <div className="mb-4 space-y-3">
            <SiteUpdatesCarouselClient featuredBrands={featuredBrands} />
            <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
              <div>
                {/* Optional title if you ever want to bring it back */}
               <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-textPrimary mb-1">
  Digital Cards, eSIMs & Mobile Top-Ups — Instantly!
</h2>

<p className="text-sm text-textSecondary max-w-xl">
Instant Delivery Digital Cards eSims and Mobile Top-Ups.
</p>


              </div>

              <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0">
                <p className="text-[11px] uppercase tracking-wide text-textSecondary/70 mb-1">
                  Start by picking a category
                </p>
                <CategoryTabs
                  selected={selectedCategory}
                  onSelect={handleCategorySelect}
                />
              </div>
            </div>
          </div>
{!isPiBrowser && (
  <div className="mb-4 px-3 py-3 text-xs sm:text-sm bg-surface/80 border border-border rounded-lg">
    <p className="text-center mb-3">
      Want to pay with Pi Coin? Open this page in Pi Browser.
    </p>
    <PiBrowserCta />
  </div>
)}
          {/* MAIN CONTENT CARD */}
          <section className="bg-surface border border-border rounded-xl p-4 sm:p-6 shadow-sm space-y-6">
            {selectedCategory === "vouchers" && (
             <VoucherSection
  initial={voucherInitial}
  brands={brands}
  selectedProduct={selectedProduct}
  onSelect={setSelectedProduct}
  piRate={piRate}
  feePercent={feePercent}
  productFilter={(p) => !isBlocked(p)}
/>
            )}

            {selectedCategory === "topups" && (
             <TopupSection
  initial={topupInitial}
  brands={brands}
  selectedProduct={selectedProduct}
  onSelect={setSelectedProduct}
  piRate={piRate}
  feePercent={feePercent}
  productFilter={(p) => !isBlocked(p)}
/>

            )}

            {selectedCategory === "esims" && (
  <EsimSectionClient
    brands={brands}
    selectedProduct={selectedProduct}
    onSelect={setSelectedProduct}
    piRate={piRate}
    feePercent={feePercent}
    productFilter={(p) => !isBlocked(p)}
     initial={esimInitial}
 initialAllOffers={esimAllOffers}
  />
)}

          </section>

          {/* SIDE DRAWER FOR SELECTED PRODUCT */}
       {selectedProduct && (
  <div className="fixed inset-0 z-50 flex items-center justify-end">
    {/* Overlay */}
    <div
      className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in transition-opacity duration-200"
      onClick={() => setSelectedProduct(null)}
    />

    {/* Drawer */}
    <div
      className="relative w-full max-w-md bg-surface text-textPrimary p-4 h-full overflow-y-auto
                 border-l border-border shadow-xl slide-in-from-right"
      onClick={(e) => e.stopPropagation()}
    >
  


                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedProduct(null);
                  }}
                  className="absolute top-3 right-3 inline-flex h-11 w-11 items-center justify-center rounded-full text-xl font-bold text-textSecondary hover:text-accent hover:bg-white/5"
                  aria-label="Close"
                  title="Close"
                >
                  &times;
                </button>

                <SelectedProductSummary
                  product={selectedProduct}
                  brands={brands}
                  priceUSD={priceUSD}
                  piRate={piRate}
                  basePI={basePI}
                  totalPI={totalPI}
                  feePercent={feePercent}
                  user={user}
                  onCancel={() => setSelectedProduct(null)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
